import React, { useCallback, useEffect } from "react";
import { createRoot, Root } from "react-dom/client";
import SnackBar from "./SnackBar";

type SnackbarVariant = "success" | "error" | "info" | "warning";

type SnackbarOptions = {
  variant?: SnackbarVariant;
  persist?: boolean;
  body?: React.ReactNode;
  /** Optional control (e.g. an "Undo" button) rendered to the right of the text. */
  action?: React.ReactNode;
};

type SnackbarItem = {
  id: string;
  title: string;
  variant: SnackbarVariant;
  persist: boolean;
  body?: React.ReactNode;
  action?: React.ReactNode;
};

let snackbarContainer: HTMLElement | null = null;
let snackbarRoot: Root | null = null;
const SNACKBAR_GAP = 10;

// Component to render all snackbars
const SnackbarStack = () => {
  return (
    <div className="fixed top-5 left-0 right-0 z-50">
      <div className="flex flex-col items-stretch max-w-screen-xl mx-auto px-4">
        {Array.from(snackbars).map((snackbar, index) => (
          <div
            key={snackbar.id}
            style={{
              transform: `translateY(${index * SNACKBAR_GAP}px)`,
            }}
            className="w-full" // Add this
          >
            <SnackBar
              title={snackbar.title}
              body={snackbar.body}
              action={snackbar.action}
              variant={snackbar.variant}
              isVisible={true}
              onClose={() => removeSnackbar(snackbar.id)}
              persist={snackbar.persist}
              fullWidth
              hook
            />
          </div>
        ))}
      </div>
    </div>
  );
};

// Maintain a list of active snackbars
const snackbars = new Set<SnackbarItem>();

const renderSnackbars = () => {
  if (!snackbarContainer) {
    // Create container with proper class
    snackbarContainer = document.createElement("div");
    snackbarContainer.id = "snackbar-root";
    snackbarContainer.className = "tw-boundary";

    // Insert at the beginning of the body
    document.body.insertBefore(snackbarContainer, document.body.firstChild);
    snackbarRoot = createRoot(snackbarContainer);
  }

  snackbarRoot?.render(<SnackbarStack />);
};

const removeSnackbar = (id: string) => {
  const snackbar = Array.from(snackbars).find((s) => s.id === id);
  if (snackbar) {
    snackbars.delete(snackbar);
    renderSnackbars();
  }
};

export function useSnackBar() {
  useEffect(() => {
    // Initialize container on mount
    renderSnackbars();

    // Cleanup on unmount
    return () => {
      if (snackbarContainer) {
        snackbarRoot?.unmount();
        snackbarContainer.remove();
        snackbarRoot = null;
        snackbarContainer = null;
      }
    };
  }, []);

  const showSnackbar = useCallback(
    (title: string, { variant = "info", persist = false, body, action }: SnackbarOptions = {}) => {
      const newSnackbar: SnackbarItem = {
        id: Math.random().toString(36).substring(7),
        title,
        variant,
        persist,
        body,
        action,
      };

      snackbars.add(newSnackbar);
      renderSnackbars();

      return () => removeSnackbar(newSnackbar.id);
    },
    [],
  );

  return showSnackbar;
}

export const clearAllSnackbars = () => {
  snackbars.clear();
  renderSnackbars();
};

export default useSnackBar;
